import {
	InputAutocomplete,
	InputSelect,
	ItemWithLabel,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import formatDateTime from '@Utils/formatDateTime';
import { timezones } from '@Utils/timeZones';
import { useFormikContext } from 'formik';
import React, { ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import { Option } from '../GeneralSettings';
import { CompanySettingsFull } from '../utils/GeneralSettingsUtils';
import useSetGeneralSettingsFields from '../utils/useSetGeneralSettingsFields';
import { CustomInfoCard } from './GeneralSettingsFields.styles';

export default function DateAndTime(): React.ReactElement {
	const { t, i18n } = useGlobalization();
	const { values } = useFormikContext<CompanySettingsFull>();
	const setGeneralSettingsFields = useSetGeneralSettingsFields();
	const companySettingsErrors =
		useSelector(getValue).companySettings.companySettingsErrors || {};

	const languageEnabled = useIsFeatureEnabled(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		'info-360-admin-language-dropdown',
	);

	return (
		<CustomInfoCard fullHeight title={t('Date and Time')}>
			<ItemWithLabel
				label={t('Current date and time')}
				value={formatDateTime(
					new Date(),
					values.timeZoneIANA,
					values.hourCycle === '12 hour clock'
						? `${values.dateFormat} - hh:mm A`
						: `${values.dateFormat} - HH:mm`,
				)}
			/>
			<InputSelect
				menuItems={[
					{ key: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
					{ key: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
				]}
				fieldAttrs={{ name: 'dateFormat' }}
				selectProps={{ label: t('Date Format') }}
				dataCy="dateFormat"
				onChange={e => {
					setGeneralSettingsFields({
						[e.target.name]: e.target.value,
					});
				}}
				networkError={companySettingsErrors.dateFormat}
			/>
			<InputSelect
				menuItems={[
					{
						key: '12 hour clock',
						value: '12 hour clock',
						label: t('12 hour clock'),
					},
					{
						key: '24 hour clock',
						value: '24 hour clock',
						label: t('24 hour clock'),
					},
				]}
				fieldAttrs={{ name: 'hourCycle' }}
				selectProps={{ label: t('Time Format') }}
				dataCy="timeFormat"
				onChange={(e: ChangeEvent<HTMLInputElement>) => {
					setGeneralSettingsFields({
						hourCycle12: e.target.value === '12 hour clock',
					});
				}}
				networkError={companySettingsErrors.hourCycle12}
			/>
			<InputAutocomplete
				fieldAttrs={{ name: 'timeZoneIANA' }}
				options={timezones.map(key => ({
					key,
					value: key.replace('_', ' '),
				}))}
				label={t('Time Zone')}
				dataCy="timeZone"
				autocompleteProps={{ disableClearable: true }}
				onChange={(
					_e: ChangeEvent<HTMLInputElement>,
					option?: Option,
				) => {
					if (option) {
						setGeneralSettingsFields({ timeZoneIANA: option.key });
					}
				}}
			/>
			{languageEnabled && (
				<InputSelect
					menuItems={[
						{
							label: t('English', {
								context: 'language dropdown > English',
							}),
							value: 'en-US',
							key: 'English',
						},
						{
							label: t('French', {
								context: 'language dropdown > French',
							}),
							value: 'fr_FR',
							key: 'French',
						},
						{
							label: t('German', {
								context: 'language dropdown > German',
							}),
							value: 'de_DE',
							key: 'German',
						},
						{
							label: t('Japanese', {
								context: 'language dropdown > Japanese',
							}),
							value: 'ja_JA',
							key: 'Japanese',
						},
					]}
					fieldAttrs={{ name: 'language' }}
					selectProps={{
						label: t('Language', {
							context: 'Language Picker Dropdown /admin page',
						}),
					}}
					dataCy="languagePicker"
					onChange={(_e: ChangeEvent<HTMLInputElement>) => {
						if (_e) {
							i18n.changeLanguage(_e.target.value);
							setGeneralSettingsFields({
								language: _e.target.value,
							});
						}
					}}
					networkError={companySettingsErrors.hourCycle12}
				/>
			)}
		</CustomInfoCard>
	);
}
